import { useState, useEffect, useRef } from 'react';
import { paginationBatchSize } from './Constants';
import { ErrorType } from './Types';

export const useResponsiveElementWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const handleResize = (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref]);

  return [ref, width] as const;
};

export const useInboxListScrollPagination = (
  listContainerElement: HTMLDivElement | null,
  hasNext: boolean,
  isLoadingNext: boolean,
  loadNext: (batchSize: number) => void
) => {
  useEffect(() => {
    const handleScrollPagination = () => {
      if (listContainerElement && hasNext) {
        const isScrolledToBottom =
          listContainerElement.clientHeight > 0 &&
          listContainerElement.scrollHeight -
            listContainerElement.scrollTop -
            10 <=
            listContainerElement.clientHeight;
        if (isScrolledToBottom) {
          loadNext(paginationBatchSize);
        }
      }
    };

    if (hasNext && listContainerElement) {
      if (
        listContainerElement.clientHeight > 0 &&
        listContainerElement.scrollHeight - listContainerElement.scrollTop <=
          listContainerElement.clientHeight
      ) {
        loadNext(paginationBatchSize);
      } else {
        listContainerElement.addEventListener('scroll', handleScrollPagination);
        listContainerElement.addEventListener('resize', handleScrollPagination);
      }
    }
    return () => {
      if (listContainerElement) {
        listContainerElement.removeEventListener(
          'scroll',
          handleScrollPagination
        );
        listContainerElement.removeEventListener(
          'resize',
          handleScrollPagination
        );
      }
    };
  }, [listContainerElement, hasNext, isLoadingNext, loadNext]);
};

export const useTenantScrollPagination = <RelayDataType>(
  data: RelayDataType,
  hasNext: boolean,
  loadNext: (batchSize: number) => void,
  isLoadingNext: boolean
) => {
  useEffect(() => {
    const handleScrollPagination = () => {
      if (
        hasNext &&
        !isLoadingNext &&
        document.body.scrollTop + window.innerHeight + 5 >=
          document.documentElement.offsetHeight
      ) {
        loadNext(paginationBatchSize);
      }
    };

    if (hasNext) {
      if (document.body.scrollHeight <= document.body.clientHeight)
        loadNext(paginationBatchSize);
      else {
        window.addEventListener('scroll', handleScrollPagination);
        window.addEventListener('resize', handleScrollPagination);
      }
    }
    return () => {
      window.removeEventListener('scroll', handleScrollPagination);
      window.removeEventListener('resize', handleScrollPagination);
    };
  }, [data, hasNext, loadNext, isLoadingNext]);
};

export const useFocusOnInitialError = <
  T extends HTMLElement = HTMLInputElement,
>(
  errors: ErrorType[],
  refArg?: React.RefObject<T>
) => {
  const internalRef = useRef<T>(null);
  const ref = refArg ?? internalRef;
  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      if (errors.length > 0 && ref.current) {
        ref.current.focus();
      }
    }
  }, [errors, ref]);

  return ref;
};

export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};
